import React, { Suspense, useEffect, useMemo } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { LanguageProvider, useLanguage } from './context/LanguageContext'
import { ThemeProvider } from './context/ThemeContext'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import Home from './features/home/Home'
import LevelsAndSubscriptions from './pages/LevelsAndSubscriptions'
import Usage from './features/stats/Usage'
import Login from './features/auth/Login';
import Signup from './features/auth/Signup';
import SignupSuccess from './features/auth/SignupSuccess';
import ResendVerification from './features/auth/ResendVerification';
import ForgotPassword from './features/auth/ForgotPassword';
import ResetPassword from './features/auth/ResetPassword';
import CompleteProfile from './features/auth/CompleteProfile';
import Partnership from './pages/Partnership';
import About from './pages/About';
import Privacy from './pages/Privacy';
import BlogList from './features/blog/BlogList';
import BlogPost from './features/blog/BlogPost';
import FaqList from './features/faq/FaqList';
import PublicContentLayout from './components/layout/PublicContentLayout';
import { AuthProvider } from './context/AuthContext';
import { ExamProvider } from './context/ExamContext';
import { QuizProvider } from './context/QuizContext';
import { CourseProvider } from './context/CourseContext';
import ProtectedRoute from './features/auth/ProtectedRoute';
import ScrollToTop from './components/layout/ScrollToTop';
import PublicLangRoute from './components/common/PublicLangRoute';
import GaPageView from './hooks/useGaPageView';


import DashboardLayout from './features/dashboard/DashboardLayout';
import LevelOverview from './features/dashboard/LevelOverview';
import OrderHistory from './features/account/OrderHistory';
import StatsOverview from './features/dashboard/StatsOverview';
import TrainingHub from './features/dashboard/TrainingHub';
import ExamSelector from './features/exams/ExamSelector';
import AdvancedStats from './features/dashboard/AdvancedStats';
import ExamResultViewer from './features/exams/ExamResultViewer';
import Account from './features/account/Account';
import AccountLayout from './features/account/AccountLayout';
import UserInfo from './features/account/UserInfo';
import Referrals from './features/account/Referrals';
import RecommendationLetter from './features/account/RecommendationLetter';
import RevisionSheets from './features/account/RevisionSheets';
import HoursRecord from './features/account/HoursRecord';
import Victories from './features/account/Victories';
import SchoolStats from './features/account/SchoolStats';
import ExamSession from './features/exams/ExamSession';
import SessionReview from './features/exams/SessionReview';
import HelpSettings from './features/account/HelpSettings';
import Notifications from './features/account/Notifications';
import Contact from './pages/Contact';
import CheckoutReturn from './components/payment/CheckoutReturn';
import { HelpProvider } from './context/HelpContext';
import { LevelsProvider } from './context/LevelsContext';
import { SeoProvider } from './hooks/useSeoConfig';
import { Helmet } from 'react-helmet-async';
import { useSeoConfig } from './hooks/useSeoConfig';
import { userKey } from './utils/userStorage';
import { ADMIN_ENABLED } from './utils/adminFeature';
const AdminDashboard = ADMIN_ENABLED
  ? React.lazy(() => import('./features/admin/AdminDashboard'))
  : null;
import ContentProtection from './components/common/ContentProtection';
import CookieBanner, { hasCookieConsent } from './components/common/CookieBanner';

// Injects global SEO meta (Google verification + Analytics) from admin config
function GlobalSeoMeta() {
    const config = useSeoConfig();
    const verification = config?.google_verification;
    const gaId = config?.google_analytics_id;

    const loadGA = (gaId) => {
        if (!gaId) return;
        const ids = gaId.split(',').map(id => id.trim()).filter(Boolean);
        if (!ids.length) return;
        if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${ids[0]}`;
            document.head.appendChild(script);
            const inline = document.createElement('script');
            inline.textContent = 'window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag("js",new Date());';
            document.head.appendChild(inline);
        }
        ids.forEach(id => { window.gtag?.('config', id); });
    };

    useEffect(() => {
        // Load GA only if user has already consented
        if (hasCookieConsent()) {
            loadGA(gaId);
        }

        // Load GA when user accepts consent during this session
        const onConsent = () => { if (hasCookieConsent()) loadGA(gaId); };
        window.addEventListener('nizus_consent_updated', onConsent);
        return () => window.removeEventListener('nizus_consent_updated', onConsent);
    }, [gaId]);

    if (!verification) return null;
    return (
        <Helmet>
            <meta name="google-site-verification" content={verification} />
        </Helmet>
    );
}

// Redirect authenticated (non-admin) users from "/:lang" to "/dashboard"
const HomeOrDashboard = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  if (isAuthenticated && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Home />;
};

// Détecte la langue préférée et redirige "/" vers "/fr" ou "/en".
// Ordre de priorité : 1) localStorage (choix précédent user) 2) Accept-Language navigateur 3) fr par défaut.
const RootLangRedirect = () => {
  const target = useMemo(() => {
    try {
      const saved = localStorage.getItem('nizus_language');
      if (saved === 'en' || saved === 'fr') return `/${saved}`;
    } catch { /* ignore */ }
    const browserLang = (navigator.language || 'fr').toLowerCase();
    return browserLang.startsWith('en') ? '/en' : '/fr';
  }, []);
  return <Navigate to={target} replace />;
};

// Legacy redirect /dashboard/blog/:slug → /dashboard/account/blog/:slug (preserves slug)
const LegacyBlogRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/dashboard/account/blog/${slug}`} replace />;
};

// URLs blog SANS préfixe de langue (/blog, /blog/:slug) — bookmarks, liens externes,
// anciens liens on-site → redirige vers /<lang>/blog[/:slug]. Même détection que RootLangRedirect.
const BlogNoLangRedirect = () => {
  const { slug } = useParams();
  const lang = useMemo(() => {
    try {
      const saved = localStorage.getItem('nizus_language');
      if (saved === 'en' || saved === 'fr') return saved;
    } catch { /* ignore */ }
    return (navigator.language || 'fr').toLowerCase().startsWith('en') ? 'en' : 'fr';
  }, []);
  return <Navigate to={slug ? `/${lang}/blog/${slug}` : `/${lang}/blog`} replace />;
};

// Reads last level from localStorage for instant redirect (before hooks mount)
const DashboardRedirect = () => {
  const target = useMemo(() => {
    try {
      const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
      if (raw) {
        const prefs = JSON.parse(raw);
        if (prefs.lastLevel) return prefs.lastLevel;
      }
    } catch { /* ignore */ }
    return 'pstar';
  }, []);
  return <Navigate to={`/dashboard/${target}`} replace />;
};

function App() {
  return (
    <LanguageProvider>
      <SeoProvider>
      <LevelsProvider>
      <ThemeProvider>
        <AuthProvider>
          <ContentProtection />
          <ExamProvider>
            <QuizProvider>
            <CourseProvider>
            <HelpProvider>
              <Router>
              <GlobalSeoMeta />
              <GaPageView />
              <ScrollToTop />
              <CookieBanner />
              <Header />
              <Routes>
                {/* Root et alias historiques /fr /en → redirige vers /<lang>/ selon prefs */}
                <Route path="/" element={<RootLangRedirect />} />

                {/* Blog sans langue → /<lang>/blog[/:slug]. La règle littérale "blog" prime sur /:lang. */}
                <Route path="/blog" element={<BlogNoLangRedirect />} />
                <Route path="/blog/:slug" element={<BlogNoLangRedirect />} />

                {/* === ROUTES PUBLIQUES PREFIXEES PAR LANGUE === */}
                {/* :lang est validé par PublicLangRoute (sinon redirect /fr/...) */}
                <Route path="/:lang" element={<PublicLangRoute><HomeOrDashboard /></PublicLangRoute>} />
                <Route path="/:lang/levels" element={<PublicLangRoute><PublicContentLayout><LevelsAndSubscriptions /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/subscriptions" element={<PublicLangRoute><Navigate to="../levels" replace /></PublicLangRoute>} />
                <Route path="/:lang/usage" element={<PublicLangRoute><Usage /></PublicLangRoute>} />
                {/* /progress et /improve : anciens raccourcis vers les sections home — redirige vers home (scroll auto perdu, acceptable). */}
                <Route path="/:lang/progress" element={<PublicLangRoute><Navigate to=".." replace /></PublicLangRoute>} />
                <Route path="/:lang/improve" element={<PublicLangRoute><Navigate to=".." replace /></PublicLangRoute>} />
                <Route path="/:lang/partnership" element={<PublicLangRoute><PublicContentLayout><Partnership /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/about" element={<PublicLangRoute><PublicContentLayout><About /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/privacy" element={<PublicLangRoute><PublicContentLayout><Privacy /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/contact" element={<PublicLangRoute><PublicContentLayout><Contact /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/blog" element={<PublicLangRoute><PublicContentLayout><BlogList /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/blog/:slug" element={<PublicLangRoute><PublicContentLayout><BlogPost /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/faq" element={<PublicLangRoute><PublicContentLayout><FaqList /></PublicContentLayout></PublicLangRoute>} />
                <Route path="/:lang/login" element={<PublicLangRoute><Login /></PublicLangRoute>} />
                <Route path="/:lang/signup" element={<PublicLangRoute><Signup /></PublicLangRoute>} />
                <Route path="/:lang/signup-success" element={<PublicLangRoute><SignupSuccess /></PublicLangRoute>} />
                <Route path="/:lang/resend-verification" element={<PublicLangRoute><ResendVerification /></PublicLangRoute>} />
                <Route path="/:lang/forgot-password" element={<PublicLangRoute><ForgotPassword /></PublicLangRoute>} />
                <Route path="/:lang/reset-password" element={<PublicLangRoute><ResetPassword /></PublicLangRoute>} />

                {/* === Zones authentifiées (langue gérée par profil user, pas par URL) === */}
                <Route path="/complete-profile" element={
                  <ProtectedRoute>
                    <CompleteProfile />
                  </ProtectedRoute>
                } />
                {ADMIN_ENABLED && AdminDashboard && (
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}>
                        <AdminDashboard />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                )}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<DashboardRedirect />} />
                  <Route path=":levelId" element={<LevelOverview />} />
                  <Route path="training" element={<TrainingHub />} />
                  <Route path="exam" element={<ExamSession />} />
                  <Route path="training/new" element={<ExamSelector />} />
                  <Route path="stats" element={<StatsOverview />} />
                  <Route path="stats/advanced" element={<AdvancedStats />} />
                  <Route path="stats/advanced" element={<AdvancedStats />} />
                  <Route path="exam-results/:id" element={<ExamResultViewer />} />
                  <Route path="review/:sessionId" element={<SessionReview />} />
                  <Route path="checkout/return" element={<CheckoutReturn />} />
                  {/* Legacy redirects: old /dashboard/blog etc. → new /dashboard/account/blog */}
                  <Route path="blog" element={<Navigate to="/dashboard/account/blog" replace />} />
                  <Route path="blog/:slug" element={<LegacyBlogRedirect />} />
                  <Route path="faq" element={<Navigate to="/dashboard/account/faq" replace />} />
                  <Route path="about" element={<Navigate to="/dashboard/account/about" replace />} />
                  <Route path="privacy" element={<Navigate to="/dashboard/account/privacy" replace />} />
                  <Route path="account" element={<AccountLayout><Outlet /></AccountLayout>}>
                    <Route index element={<Account />} />
                    <Route path="orders" element={<OrderHistory />} />
                    <Route path="victories" element={<Victories />} />
                    <Route path="infos" element={<UserInfo />} />
                    <Route path="parrainages" element={<Referrals />} />
                    <Route path="recommendation" element={<RecommendationLetter />} />
                    <Route path="revision-sheets" element={<RevisionSheets />} />
                    <Route path="hours-record" element={<HoursRecord />} />
                    <Route path="school-stats" element={<SchoolStats />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="messages" element={<Navigate to="/dashboard/account/notifications" replace />} />
                    <Route path="help" element={<HelpSettings />} />
                    {/* Content pages now live under /account/ so back button returns to /account naturally.
                        Order: most specific paths (with :slug) listed first as a safety belt — React Router v6
                        is supposed to rank by specificity but explicit ordering avoids any edge case. */}
                    <Route path="blog/:slug" element={<BlogPost />} />
                    <Route path="blog" element={<BlogList />} />
                    <Route path="faq" element={<FaqList />} />
                    <Route path="about" element={<About />} />
                    <Route path="privacy" element={<Privacy />} />
                  </Route>
                </Route>
              </Routes>
              <Footer />
              </Router>
            </HelpProvider>
            </CourseProvider>
            </QuizProvider>
          </ExamProvider>
        </AuthProvider>
      </ThemeProvider>
      </LevelsProvider>
      </SeoProvider>
    </LanguageProvider>
  )
}

export default App
